import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable max-len */
import { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '@mcn-platform/models';
import { Search, sharedConversationMeta, ConversationStoreProvider, resolveDualModeCardConfig, resolveDualSearchEnabled, resolveTopSectionConfig, resolveLayoutConfig, } from '@mcn-platform/converse-lib';
import ConversationStoreAdapter from './ConversationStoreAdapter';
import assetGetData from '../common/asset/assetGetData';
const SearchWidget = ({ searchQuery, adminOptions, agentAvatarImage, agentName, bodyStyle, clientAIAnimatedProcessingIcon, courseCardShowDescLines, courseCardShowImage, courseCardShowInstructor, courseCardShowTitle, enableMultiRefinements, enableVoiceInput, allowCollapseSearchWidget, disableInternalScroll, showPersistentPrompts, feedbackButton, hideRightRailOnMobile, inputStyle, maxItemsInRightRail, multiRefinementSubmitButtonText, openWebPagesInNewTab, questionAlignment, questionStyle, referenceFormatting, resetConversationLabel, responseAlignment, responseStyle, sendImage, showItemReferencesWithMessage = true, showResetWithInput, startingMessageFontStyle, userAvatarImage, 
// Legacy props
productFormatting, dualModeEnabled, topSectionMaxHeightPx, itemReferenceDisplay, 
// New semantic props for visual parameters
disableArrowScroll, disableAddSpaceAnimation, topSectionMaxHeight, cardMaxHeight, cardShowDescription, cardShowPrice, cardShowAddToCart, cardPriceBelowTitle, cardOrientation, hideFirstMessage, disableAutoScroll, layoutMode, stripTrailingAsterisk, }) => {
    const [ready, setReady] = useState(false);
    const [clientAIAnimatedProcessingIconImage, setClientAIAnimatedProcessingIconImage] = useState(undefined);
    const [agentAvatarImageImage, setAgentAvatarImageImage] = useState(undefined);
    const [userAvatarImageImage, setUserAvatarImageImage] = useState(undefined);
    const [sendImageImage, setSendImageImage] = useState(undefined);
    const store = useStore();
    useEffect(() => {
        const load = async () => {
            const data = await SearchWidget.getData({
                clientAIAnimatedProcessingIcon,
                agentAvatarImage,
                userAvatarImage,
                sendImage,
            }, store);
            setClientAIAnimatedProcessingIconImage(data.clientAIAnimatedProcessingIcon);
            setAgentAvatarImageImage(data.agentAvatarImage);
            setUserAvatarImageImage(data.userAvatarImage);
            setSendImageImage(data.sendImage);
            setReady(true);
            return data;
        };
        load();
    }, [
        clientAIAnimatedProcessingIcon,
        agentAvatarImage,
        userAvatarImage,
        sendImage,
        store,
    ]);
    // Resolve dual search enabled state from portal config
    const portalConfig = store.currentPortal?.clientAIConfig;
    const isDualModeEnabled = resolveDualSearchEnabled(portalConfig);
    const portalDualMode = isDualModeEnabled;
    const designerLayoutMode = layoutMode === 'dual';
    const isDual = portalDualMode && designerLayoutMode;
    const messageStackMode = isDual ? 'natural' : 'fill';
    // Calculate collapseAllowed from props and portal config
    const collapseAllowed = allowCollapseSearchWidget
        ?? portalConfig?.allowCollapseSearchWidget ?? false;
    // Resolve all configs from Portal + Designer props
    const resolvedLayoutConfig = useMemo(() => resolveLayoutConfig(portalConfig?.aiSearchConfig?.layout, 
    // NO embedConfig - Portal has all general configs
    {
        topSectionMaxHeight,
    }), [portalConfig?.aiSearchConfig?.layout, topSectionMaxHeight]);
    const resolvedTopSectionConfig = useMemo(() => resolveTopSectionConfig(portalConfig?.aiSearchConfig?.topSection, 
    // NO embedConfig - Portal has all general configs
    {
        topSectionMaxHeight: topSectionMaxHeight ?? topSectionMaxHeightPx,
    }), [
        portalConfig?.aiSearchConfig?.topSection,
        topSectionMaxHeight,
        topSectionMaxHeightPx,
    ]);
    // Calculate final productFormatting using helper function
    // Supports semantic props and legacy productFormatting prop
    const finalProductFormatting = useMemo(() => {
        // If dual mode is enabled, use resolveDualModeCardConfig
        if (isDual) {
            // Map legacy productFormatting to semantic props if needed
            const semanticProps = {
                cardMaxHeight: cardMaxHeight ?? productFormatting?.maxHeight,
                cardShowDescription: cardShowDescription ?? productFormatting?.showDescription,
                cardShowPrice: cardShowPrice ?? productFormatting?.showPrice,
                cardShowAddToCart: cardShowAddToCart ?? productFormatting?.showAddToCartButton,
                cardPriceBelowTitle,
                cardOrientation: cardOrientation ?? productFormatting?.orientation,
            };
            const portalCardConfig = portalConfig?.aiSearchConfig?.dualModeCardConfig;
            const resolved = resolveDualModeCardConfig(portalCardConfig, semanticProps);
            // Merge with any additional productFormatting props
            return {
                ...resolved,
                ...productFormatting,
                showBox: true,
                // Ensure these are always set for dual mode
                addToCart: false,
                clickBehavior: 'callback',
                cursorStyle: 'pointer',
                cardInteractive: true,
            };
        }
        // Non-dual mode: apply search mode defaults but keep productFormatting overrides
        const result = {
            ...productFormatting,
            // Search mode specific behavior
            addToCart: false,
            clickBehavior: 'callback',
            cursorStyle: 'pointer',
            cardInteractive: true,
        };
        return result;
    }, [
        isDual,
        productFormatting,
        portalConfig,
        cardMaxHeight,
        cardShowDescription,
        cardShowPrice,
        cardShowAddToCart,
        cardPriceBelowTitle,
        cardOrientation,
    ]);
    // Calculate semantic props (NO generic flags)
    // SearchWidget is the ONLY place where decisions are made
    const semanticProps = useMemo(() => ({
        // Behavior props (semantic, not generic flags)
        // Use widget props if provided, otherwise default to isDual
        hideFirstMessage: hideFirstMessage ?? isDual,
        disableAutoScroll: disableAutoScroll ?? isDual,
        // Layout props (semantic)
        layoutMode: (isDual ? 'dual' : 'ai'), // Only for structure determination, NOT for logic
        // Layout configs from resolved config
        initialHeight: resolvedLayoutConfig.initialHeight,
        headerHeightValue: resolvedLayoutConfig.headerHeightValue,
        headerCssVariable: resolvedLayoutConfig.headerCssVariable,
        useCssVariable: resolvedLayoutConfig.useCssVariable,
        allowCollapseSearchWidget: collapseAllowed,
        // Top section config
        topSectionConfig: resolvedTopSectionConfig,
    }), [isDual, hideFirstMessage, disableAutoScroll, resolvedLayoutConfig, resolvedTopSectionConfig, collapseAllowed]);
    if (!ready) {
        return null;
    }
    return (_jsx(ConversationStoreProvider, { value: new ConversationStoreAdapter(store), children: _jsx(Search, { searchQuery: searchQuery, adminOptions: adminOptions, agentAvatarImage: agentAvatarImageImage, agentName: agentName, animatedProcessingImage: clientAIAnimatedProcessingIconImage, bodyStyle: bodyStyle, courseCardShowDescLines: courseCardShowDescLines, courseCardShowImage: courseCardShowImage, courseCardShowInstructor: courseCardShowInstructor, courseCardShowTitle: courseCardShowTitle, enableMultiRefinements: enableMultiRefinements, enableVoiceInput: enableVoiceInput, feedbackButton: feedbackButton, hideRightRailOnMobile: hideRightRailOnMobile, inputStyle: inputStyle, maxItemsInRightRail: maxItemsInRightRail, multiRefinementSubmitButtonText: multiRefinementSubmitButtonText, openWebPagesInNewTab: openWebPagesInNewTab, questionAlignment: questionAlignment, questionStyle: questionStyle, referenceFormatting: referenceFormatting, resetConversationLabel: resetConversationLabel, responseAlignment: responseAlignment, responseStyle: responseStyle, sendImage: sendImageImage, showItemReferencesWithMessage: showItemReferencesWithMessage, showResetWithInput: showResetWithInput, startingMessageFontStyle: startingMessageFontStyle, userAvatarImage: userAvatarImageImage, productFormatting: finalProductFormatting, 
            // Semantic props (NO generic flags)
            hideFirstMessage: semanticProps.hideFirstMessage, disableAutoScroll: semanticProps.disableAutoScroll, layoutMode: semanticProps.layoutMode, topSectionConfig: semanticProps.topSectionConfig, initialHeight: semanticProps.initialHeight, headerHeightValue: semanticProps.headerHeightValue, headerCssVariable: semanticProps.headerCssVariable, useCssVariable: semanticProps.useCssVariable, allowCollapseSearchWidget: semanticProps.allowCollapseSearchWidget, disableInternalScroll: disableInternalScroll, showPersistentPrompts: showPersistentPrompts, stripTrailingAsterisk: stripTrailingAsterisk, 
            // Legacy props (backward compatibility - will be removed in future)
            disableAddSpaceAnimation: isDual ? true : disableAddSpaceAnimation, disableArrowScroll: disableArrowScroll, itemReferenceDisplay: itemReferenceDisplay, messageStackMode: messageStackMode }) }));
};
const getImage = async (keyOrId, size, store) => {
    if (!keyOrId) {
        return undefined;
    }
    const imageAsset = await assetGetData(keyOrId, size, store);
    return {
        url: imageAsset.url,
        status: imageAsset.status,
        alt: imageAsset.alt,
        className: imageAsset.className,
        avatar: imageAsset.avatar,
    };
};
SearchWidget.getData = async ({ clientAIAnimatedProcessingIcon, agentAvatarImage, userAvatarImage, sendImage, }, store) => {
    const [clientAIAnimatedProcessingIconData, agentAvatarImageData, userAvatarImageData, sendImageData,] = await Promise.all([
        getImage(clientAIAnimatedProcessingIcon, 'original', store),
        getImage(agentAvatarImage, 'original', store),
        getImage(userAvatarImage, 'original', store),
        getImage(sendImage, 'original', store),
    ]);
    return {
        clientAIAnimatedProcessingIcon: clientAIAnimatedProcessingIconData,
        agentAvatarImage: agentAvatarImageData,
        userAvatarImage: userAvatarImageData,
        sendImage: sendImageData,
    };
};
SearchWidget.defaultProps = {
    ...Search.defaultProps,
    allowCollapseSearchWidget: false,
    disableInternalScroll: false,
    disableArrowScroll: false,
    cardPriceBelowTitle: true,
    stripTrailingAsterisk: false,
};
SearchWidget.meta = {
    group: 'portal',
    name: 'AI Search',
    sort: 40,
    description: 'Search widget for AI.',
    props: [
        ...sharedConversationMeta.filter((prop) => prop?.key !== 'seeMoreButtonLabel'),
        {
            key: 'disableInternalScroll',
            name: 'Disable Internal Scroll',
            type: 'boolean',
            default: false,
            group: 'config',
            description: 'Disable internal scroll in the left panel (dual mode).',
        },
        {
            key: 'disableArrowScroll',
            name: 'Disable Scroll Down Arrow',
            type: 'boolean',
            default: false,
            group: 'config',
            description: 'Hide the scroll-down arrow in the conversation panel.',
        },
        {
            key: 'cardPriceBelowTitle',
            name: 'Price Below Title',
            type: 'boolean',
            default: true,
            group: 'config',
            description: 'Place price directly under the title in dual search product cards.',
        },
        {
            key: 'allowCollapseSearchWidget',
            name: 'Toggle Search Enabled',
            type: 'boolean',
            default: false,
            group: 'config',
            description: 'Allow the dual-mode search widget to show the See More/See Less collapse toggle and AI Search toggle button.',
        },
        {
            key: 'stripTrailingAsterisk',
            name: 'Strip Trailing *',
            type: 'boolean',
            default: false,
            group: 'config',
            description: 'Remove a trailing * from the incoming search query.',
        },
    ],
    defaultConfig: {
        type: 'component',
        component: 'SearchWidget',
        props: SearchWidget.defaultProps,
    },
};
export default observer(SearchWidget);
//# sourceMappingURL=SearchWidget.js.map